import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {Download, FileText, Image as ImageIcon, Paperclip, RefreshCw, Trash2} from "lucide-react";
import {
  ChangeEvent,
  DragEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {toast} from "react-toastify";
import {
  getNoteAttachmentDownload,
  readNoteAttachment,
  removeStagedNoteAttachment,
  stageNoteAttachment,
} from "../api";
import {wmErrorMessage} from "../api/wm-client";
import {NoteAttachmentContext, NoteAttachmentView} from "../dtos";
import {
  NOTE_ATTACHMENT_ACCEPT,
  createNoteAttachmentUploadQueue,
  validateNoteAttachmentFiles,
} from "../utils/note-attachments";
import {secondaryButtonSx} from "./field-shell";

type DraftItem = {
  localUid: string;
  file: File;
  status: "queued" | "uploading" | "ready" | "error";
  attachment?: NoteAttachmentView;
  error?: string;
};

export interface NoteAttachmentDraft {
  items: DraftItem[];
  add: (files: File[]) => void;
  retry: (localUid: string) => void;
  remove: (localUid: string) => void;
  discard: () => void;
  markSubmitted: () => void;
  attachmentUids: string[];
  pending: boolean;
  hasErrors: boolean;
}

let nextLocalUid = 0;

// This hook shares the attachment lifecycle with the composer and reader exported below.
// eslint-disable-next-line react-refresh/only-export-components
export const useNoteAttachmentDraft = ({
  caseUid,
  context,
}: {
  caseUid?: string | null;
  context?: NoteAttachmentContext | null;
}): NoteAttachmentDraft => {
  const [items, setItems] = useState<DraftItem[]>([]);
  const itemsRef = useRef(items);
  const removed = useRef(new Set<string>());
  const epoch = useRef(0);
  const queue = useRef(createNoteAttachmentUploadQueue(3));
  itemsRef.current = items;

  const removeRemote = useCallback((attachment: NoteAttachmentView) => {
    void removeStagedNoteAttachment(attachment.caseUid, attachment.uid).catch(() => undefined);
  }, []);

  const updateItem = useCallback((localUid: string, changes: Partial<DraftItem>) => {
    setItems((current) =>
      current.map((item) => item.localUid === localUid ? {...item, ...changes} : item)
    );
  }, []);

  const start = useCallback((localUid: string, file: File) => {
    if (!caseUid || !context) return;
    const startedEpoch = epoch.current;
    updateItem(localUid, {status: "uploading", error: undefined});
    queue.current
      .run(
        () => stageNoteAttachment(caseUid, file, context),
        (attachment) => removeStagedNoteAttachment(attachment.caseUid, attachment.uid)
      )
      .then((attachment) => {
        if (!attachment) return;
        if (startedEpoch !== epoch.current || removed.current.has(localUid)) {
          removeRemote(attachment);
          return;
        }
        updateItem(localUid, {status: "ready", attachment});
      })
      .catch((error) => {
        if (startedEpoch !== epoch.current || removed.current.has(localUid)) return;
        updateItem(localUid, {status: "error", error: wmErrorMessage(error)});
      });
  }, [caseUid, context, removeRemote, updateItem]);

  const add = useCallback((files: File[]) => {
    if (!caseUid || !context || !files.length) return;
    const countableItems = itemsRef.current.filter((item) => item.file.size > 0);
    const {accepted, errors} = validateNoteAttachmentFiles(countableItems, files);
    const additions = accepted.map((file) => ({
      localUid: `note-file-${++nextLocalUid}`,
      file,
      status: "queued" as const,
    }));
    setItems((current) => {
      const nextItems = [
        ...current,
        ...additions,
        ...errors.map((error, index) => ({
        localUid: `note-error-${++nextLocalUid}-${index}`,
        file: new File([], "Attachment"),
        status: "error" as const,
        error,
        })),
      ];
      itemsRef.current = nextItems;
      return nextItems;
    });
    additions.forEach((item) => start(item.localUid, item.file));
  }, [caseUid, context, start]);

  const remove = useCallback((localUid: string) => {
    removed.current.add(localUid);
    const item = itemsRef.current.find((candidate) => candidate.localUid === localUid);
    if (item?.attachment) removeRemote(item.attachment);
    setItems((current) => {
      const nextItems = current.filter((candidate) => candidate.localUid !== localUid);
      itemsRef.current = nextItems;
      return nextItems;
    });
  }, [removeRemote]);

  const discard = useCallback(() => {
    epoch.current += 1;
    queue.current.discard();
    const discardedItems = itemsRef.current;
    itemsRef.current = [];
    discardedItems.forEach((item) => {
      removed.current.add(item.localUid);
      if (item.attachment) removeRemote(item.attachment);
    });
    setItems([]);
  }, [removeRemote]);

  const markSubmitted = useCallback(() => {
    epoch.current += 1;
    queue.current.discard();
    itemsRef.current = [];
    setItems([]);
  }, []);

  const contextKey = JSON.stringify(context ?? null);
  useEffect(() => discard, [caseUid, contextKey, discard]);

  return {
    items,
    add,
    retry: (localUid) => {
      const item = itemsRef.current.find((candidate) => candidate.localUid === localUid);
      if (item) start(item.localUid, item.file);
    },
    remove,
    discard,
    markSubmitted,
    attachmentUids: items.flatMap((item) => item.attachment?.uid ? [item.attachment.uid] : []),
    pending: items.some((item) => item.status === "queued" || item.status === "uploading"),
    hasErrors: items.some((item) => item.status === "error"),
  };
};

const DraftImageThumbnail = ({file}: {file: File}) => {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    const objectUrl = URL.createObjectURL(file);
    setSrc(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);
  return src ? (
    <Box component="img" src={src} alt="" sx={{width: 36, height: 36, borderRadius: 1, objectFit: "cover"}} />
  ) : <ImageIcon size={18} />;
};

const DraftFileIcon = ({item}: {item: DraftItem}) => {
  const theme = useTheme();
  const pending = item.status === "uploading" || item.status === "queued";
  if (item.file.type.startsWith("image/")) {
    return (
      <Box sx={{position: "relative", width: 36, height: 36, flexShrink: 0}}>
        <DraftImageThumbnail file={item.file} />
        {pending && (
          <CircularProgress
            size={14}
            sx={{position: "absolute", right: -4, bottom: -4, bgcolor: "background.paper", borderRadius: "50%"}}
          />
        )}
      </Box>
    );
  }
  if (pending) return <CircularProgress size={14} />;
  const color = item.status === "error" ? theme.nawy.colors.semantic.text.extension.negative : undefined;
  return <FileText size={14} color={color} />;
};

export const NoteAttachmentComposer = ({
  draft,
  disabled = false,
}: {
  draft: NoteAttachmentDraft;
  disabled?: boolean;
}) => {
  const theme = useTheme();
  const input = useRef<HTMLInputElement>(null);
  const root = useRef<HTMLDivElement>(null);
  const addList = (list: FileList | null) => {
    if (!disabled) draft.add(list ? Array.from(list) : []);
  };
  const stopDrop = (event: DragEvent<HTMLDivElement>) => event.preventDefault();
  const drop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (disabled) return;
    addList(event.dataTransfer.files);
  };
  const pick = (event: ChangeEvent<HTMLInputElement>) => {
    addList(event.target.files);
    event.target.value = "";
  };
  useEffect(() => {
    const parent = root.current?.parentElement;
    if (!parent) return;
    const paste = (event: globalThis.ClipboardEvent) => {
      if (!event.clipboardData) return;
      const files = Array.from(event.clipboardData.items)
        .filter((item) => item.kind === "file")
        .flatMap((item) => item.getAsFile() ?? []);
      if (!files.length) return;
      event.preventDefault();
      event.stopPropagation();
      if (!disabled) draft.add(files);
    };
    parent.addEventListener("paste", paste);
    return () => parent.removeEventListener("paste", paste);
  }, [disabled, draft]);

  return (
    <Box
      ref={root}
      data-testid="note-attachment-composer"
      onDragOver={stopDrop}
      onDrop={drop}
      sx={{mt: 1, p: 1, border: `1px dashed ${theme.nawy.colors.semantic.border.primary}`, borderRadius: 2}}
    >
      <Button
        size="small"
        variant="outlined"
        startIcon={<Paperclip size={14} />}
        disabled={disabled}
        onClick={() => input.current?.click()}
        sx={secondaryButtonSx(theme)}
      >
        Attach files
      </Button>
      <Typography variant="caption" sx={{ml: 1, color: theme.nawy.colors.semantic.text.tertiary}}>
        Drop files or paste a screenshot · 20 files · 100 MB each
      </Typography>
      <input ref={input} hidden multiple type="file" accept={NOTE_ATTACHMENT_ACCEPT} disabled={disabled} onChange={pick} />
      {draft.items.length > 0 && (
        <Stack spacing={0.5} sx={{mt: 1}}>
          {draft.items.map((item) => (
            <Stack key={item.localUid} direction="row" spacing={1} alignItems="center">
              <DraftFileIcon item={item} />
              <Box sx={{flex: 1, minWidth: 0}}>
                <Typography variant="caption" noWrap>{item.file.name}</Typography>
                {item.error && (
                  <Typography variant="caption" sx={{display: "block", color: theme.nawy.colors.semantic.text.extension.negative}}>
                    {item.error}
                  </Typography>
                )}
              </Box>
              {item.status === "error" && item.file.size > 0 && (
                <IconButton size="small" disabled={disabled} aria-label={`Retry ${item.file.name}`} onClick={() => {
                  if (!disabled) draft.retry(item.localUid);
                }}>
                  <RefreshCw size={14} />
                </IconButton>
              )}
              <IconButton size="small" disabled={disabled} aria-label={`Remove ${item.file.name}`} onClick={() => {
                if (!disabled) draft.remove(item.localUid);
              }}>
                <Trash2 size={14} />
              </IconButton>
            </Stack>
          ))}
        </Stack>
      )}
    </Box>
  );
};

const previewable = (attachment: NoteAttachmentView): boolean =>
  attachment.mimeType.startsWith("image/") || attachment.mimeType === "application/pdf";

const attachmentUrl = async (
  attachment: NoteAttachmentView
): Promise<{url: string; temporary: boolean}> => {
  const descriptor = await getNoteAttachmentDownload(attachment.caseUid, attachment.uid);
  if (descriptor.url) return {url: descriptor.url, temporary: false};
  return {
    url: URL.createObjectURL(await readNoteAttachment(attachment.caseUid, attachment.uid)),
    temporary: true,
  };
};

const openAttachment = async (attachment: NoteAttachmentView, preview: boolean): Promise<void> => {
  const resolved = await attachmentUrl(attachment);
  const anchor = document.createElement("a");
  anchor.href = resolved.url;
  anchor.rel = "noopener";
  if (preview) anchor.target = "_blank";
  else anchor.download = attachment.fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  if (resolved.temporary) window.setTimeout(() => URL.revokeObjectURL(resolved.url), 60_000);
};

const ImageThumbnail = ({attachment}: {attachment: NoteAttachmentView}) => {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    let temporary: string | null = null;
    void attachmentUrl(attachment).then((resolved) => {
      if (!live) {
        if (resolved.temporary) URL.revokeObjectURL(resolved.url);
        return;
      }
      if (resolved.temporary) temporary = resolved.url;
      setSrc(resolved.url);
    }).catch(() => undefined);
    return () => {
      live = false;
      if (temporary) URL.revokeObjectURL(temporary);
    };
  }, [attachment]);
  return src ? (
    <Box component="img" src={src} alt="" sx={{width: 36, height: 36, borderRadius: 1, objectFit: "cover"}} />
  ) : <ImageIcon size={18} />;
};

export const NoteAttachmentList = ({attachments}: {attachments?: NoteAttachmentView[]}) => {
  const theme = useTheme();
  const rows = useMemo(() => attachments ?? [], [attachments]);
  if (!rows.length) return null;
  const open = (attachment: NoteAttachmentView, preview: boolean): void => {
    void openAttachment(attachment, preview).catch((error) => {
      toast.error(wmErrorMessage(error));
    });
  };
  return (
    <Stack spacing={0.5} sx={{mt: 0.75}}>
      {rows.map((attachment) => (
        <Stack
          key={attachment.uid}
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{p: 0.75, borderRadius: 1.5, border: `1px solid ${theme.nawy.colors.semantic.border.secondary}`}}
        >
          {attachment.mimeType.startsWith("image/") ? <ImageThumbnail attachment={attachment} /> : <FileText size={18} />}
          <Box sx={{flex: 1, minWidth: 0}}>
            <Typography variant="caption" noWrap sx={{display: "block", fontWeight: 600}}>{attachment.fileName}</Typography>
            <Typography variant="caption" color="text.secondary">{(attachment.fileSize / 1024).toFixed(0)} KB</Typography>
          </Box>
          {previewable(attachment) && (
            <Button size="small" onClick={() => open(attachment, true)}>Preview</Button>
          )}
          <IconButton size="small" aria-label={`Download ${attachment.fileName}`} onClick={() => open(attachment, false)}>
            <Download size={15} />
          </IconButton>
        </Stack>
      ))}
    </Stack>
  );
};
